import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, CircularProgress, IconButton, Link, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';

import { ApplicationStatus } from '@/utils/constants';
import { getApplicationHref } from '@/utils/applicationUtil';
import { ProgramNames } from '@/models/Program';
import { Translations } from '@/utils/translations/Translations';

import { ApplicantDashboardRow } from '../ApplicantDashboardTypes';

interface UseDashboardColumnsProps {
  translatedText: Translations;
  selectedProgramId: ProgramNames;
  downloading: boolean;
  downloadingAppId: number | null;
  taigaColor: string;
  handleDownloadApplication: (applicationId: number) => void;
}

function useDashboardColumns({
  translatedText,
  selectedProgramId,
  downloading,
  downloadingAppId,
  taigaColor,
  handleDownloadApplication,
}: UseDashboardColumnsProps): GridColDef[] {
  return useMemo(() => {
    return [
      {
        field: translatedText.dashboard.viewApplication,
        headerName: '',
        minWidth: 155,
        flex: 0.75,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        resizable: false,
        sortComparator: (v1) => {
          return v1 == null ? -1 : 1;
        },
        renderCell: (params: GridRenderCellParams) => {
          const dashRow = params.row as ApplicantDashboardRow;
          const appExists = dashRow.applicationId != null && dashRow.applicationTypeId != null;
          const toolTipTitle = appExists
            ? null
            : translatedText.dashboard.viewApplicationHelperTextDisabled;

          return (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tooltip title={toolTipTitle} arrow>
                <Link
                  role='button'
                  data-size='compact'
                  href={getApplicationHref(
                    dashRow.applicationId,
                    dashRow.applicationTypeId,
                    selectedProgramId
                  )}
                  aria-disabled={!appExists}
                  sx={!appExists ? { pointerEvents: 'none', opacity: 0.3 } : undefined}
                  tabIndex={!appExists ? -1 : 0}
                >
                  {translatedText.dashboard.viewApplication}
                </Link>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'applicationCycle',
        headerName: translatedText.dashboard.fieldApplicationCycle,
        minWidth: 130,
        flex: 0.7,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'statusDescription',
        headerName: translatedText.dashboard.fieldStatus,
        minWidth: 210,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'applicationType',
        headerName: translatedText.dashboard.fieldApplication,
        minWidth: 170,
        flex: 0.85,
      },
      {
        field: 'submittedBy',
        headerName: translatedText.dashboard.fieldSubmittedBy,
        minWidth: 170,
        flex: 0.85,
      },
      {
        field: 'Download',
        headerName: translatedText.dashboard.download,
        renderCell: (params: GridRenderCellParams) => {
          const dashRow = params.row as ApplicantDashboardRow;

          return dashRow.applicationId != null ? (
            <Tooltip title={translatedText.dashboard.downloadTooltip}>
              <>
                {(!downloading || downloadingAppId != dashRow.applicationId) && (
                  <IconButton
                    onClick={() => {
                      if (dashRow.applicationId != null) {
                        handleDownloadApplication(dashRow.applicationId);
                      }
                    }}
                    disabled={dashRow.status == ApplicationStatus.InProgress}
                    sx={{
                      opacity: 1,
                      fillOpacity: 1,
                      color: taigaColor,
                      marginLeft: '10px',
                    }}
                  >
                    <FileDownloadIcon />
                  </IconButton>
                )}
                {downloading && downloadingAppId == dashRow.applicationId && (
                  <div style={{ marginLeft: '20px' }}>
                    <CircularProgress size='16px' />
                  </div>
                )}
              </>
            </Tooltip>
          ) : (
            <></>
          );
        },
        width: 110,
      },
      {
        field: 'status',
        headerName: translatedText.dashboard.fieldStatus,
        width: 5,
      },
      {
        field: 'applicationTypeId',
        headerName: 'applicationTypeId',
        width: 5,
      },
    ];
  }, [
    translatedText,
    selectedProgramId,
    downloading,
    downloadingAppId,
    handleDownloadApplication,
    taigaColor,
  ]);
}

export default useDashboardColumns;
